import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import portfolioService from '../../../utils/portfolioService';

const PortfolioGallery = ({ profile, isPublicView = false }) => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedItem, setSelectedItem] = useState(null);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [portfolioItems, setPortfolioItems] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [touchStartX, setTouchStartX] = useState(null);
  const ITEMS_PER_PAGE = 9;
  
  // Fetch portfolio items from database when profile changes
  useEffect(() => {
    const fetchPortfolioItems = async () => {
      if (!profile?.id) {
        return;
      }
      
      setIsLoading(true);
      try {
        const { data, error } = await portfolioService.getPortfolioItems(profile.id, {
          category: selectedCategory !== 'all' ? selectedCategory : undefined,
          sortBy: 'featured'
        });
          
        if (error) throw error;
        setPortfolioItems(data || []);
      } catch (error) {
        console.error('Error fetching portfolio items:', error);
        // Fallback to profile.portfolio_items if available
        if (profile?.portfolio_items && Array.isArray(profile.portfolio_items)) {
          setPortfolioItems(profile.portfolio_items);
        } else {
          setPortfolioItems([]);
        }
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchPortfolioItems();
    setCurrentPage(1); // Reset to first page when category changes
  }, [profile, selectedCategory]);

  // Extract unique categories from portfolio items
  const categories = useMemo(() => {
    const categorySet = new Set();
    categorySet.add('all');
    
    portfolioItems.forEach(item => {
      if (item.category) {
        categorySet.add(item.category);
      }
    });
    
    return Array.from(categorySet);
  }, [portfolioItems]);
  
  // Filter items based on selected category
  const filteredItems = useMemo(() => {
    return selectedCategory === 'all' 
      ? portfolioItems 
      : portfolioItems.filter(item => item.category === selectedCategory);
  }, [portfolioItems, selectedCategory]);

  // Calculate pagination values
  const totalPages = Math.ceil(filteredItems.length / ITEMS_PER_PAGE);
  const paginatedItems = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return filteredItems.slice(startIndex, endIndex);
  }, [filteredItems, currentPage]);

  const openLightbox = useCallback((item) => {
    setSelectedItem(item);
    setIsLightboxOpen(true);
    document.body.style.overflow = 'hidden';
  }, []);

  const closeLightbox = useCallback(() => {
    setIsLightboxOpen(false);
    setSelectedItem(null);
    document.body.style.overflow = 'unset';
  }, []);

  const navigateLightbox = useCallback((direction) => {
    if (!selectedItem || !filteredItems.length) return;
    
    const currentIndex = filteredItems.findIndex(item => item.id === selectedItem.id);
    if (currentIndex === -1) return;
    
    let newIndex;
    
    if (direction === 'next') {
      newIndex = (currentIndex + 1) % filteredItems.length;
    } else {
      newIndex = currentIndex === 0 ? filteredItems.length - 1 : currentIndex - 1;
    }
    
    setSelectedItem(filteredItems[newIndex]);
  }, [filteredItems, selectedItem]);

  const getProjectTypeIcon = useMemo(() => {
    return (type) => {
      if (!type) return 'Folder';
      
      const typeStr = typeof type === 'string' ? type.toLowerCase() : '';
      
      switch (typeStr) {
        case 'website': return 'Globe';
        case 'web': return 'Globe';
        case 'design': return 'Figma';
        case 'document': return 'FileText';
        case 'presentation': return 'Layout';
        case 'social': return 'Share2';
        case 'social media': return 'Share2';
        case 'video': return 'Video';
        case 'app': return 'Smartphone';
        case 'mobile': return 'Smartphone';
        case 'data': return 'BarChart';
        case 'research': return 'Search';
        case 'writing': return 'Edit';
        case 'content': return 'FileText';
        default: return 'Folder';
      }
    };
  }, []);

  // Build gallery: thumbnail first, then any extra images (deduped)
  const galleryImages = useMemo(() => {
    if (!selectedItem) return [];
    const thumb = selectedItem.thumbnail || selectedItem.image_url;
    const extras = Array.isArray(selectedItem.images) ? selectedItem.images : [];
    const all = thumb ? [thumb, ...extras.filter(img => img !== thumb)] : [...extras];
    return all.filter(Boolean);
  }, [selectedItem]);

  // Reset image index when selected item changes
  useEffect(() => {
    setCurrentImageIndex(0);
  }, [selectedItem]);

  const handleImagePrev = useCallback(() => {
    setCurrentImageIndex(prev => (prev > 0 ? prev - 1 : 0));
  }, []);

  const handleImageNext = useCallback((max) => {
    setCurrentImageIndex(prev => (prev < max - 1 ? prev + 1 : max - 1));
  }, []);

  const handleTouchStart = useCallback((e) => {
    setTouchStartX(e.touches[0].clientX);
  }, []);

  const handleTouchEnd = useCallback((e, max) => {
    if (touchStartX === null) return;
    const delta = touchStartX - e.changedTouches[0].clientX;
    if (Math.abs(delta) > 50) {
      if (delta > 0) handleImageNext(max);
      else handleImagePrev();
    }
    setTouchStartX(null);
  }, [touchStartX, handleImageNext, handleImagePrev]);

  return (
    <section className="content-container py-12" id="portfolio">
      <div className="flex items-center gap-3 mb-6">
        <h2 className="text-2xl font-bold">Portfolio</h2>
        <span className="text-sm text-text-secondary">
          {!isLoading && `${filteredItems.length} item${filteredItems.length !== 1 ? 's' : ''}`}
        </span>
      </div>
      
      {isLoading ? (
        <div className="bg-white rounded-lg shadow-sm p-6 text-center">
          <div className="animate-pulse flex flex-col items-center">
            <div className="rounded-full bg-secondary-200 h-12 w-12 mb-4"></div>
            <div className="h-4 bg-secondary-200 rounded w-1/3 mb-3"></div>
            <div className="h-3 bg-secondary-200 rounded w-1/2 mb-4"></div>
          </div>
        </div>
      ) : portfolioItems.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm p-6 text-center">
          <Icon name="Folder" size={40} className="text-secondary-300 mx-auto mb-3" />
          <h3 className="text-lg font-medium mb-2">No portfolio items added yet</h3>
          <p className="text-text-secondary mb-4">Portfolio projects will appear here once added.</p>
        </div>
      ) : (
        <div>
          {/* Category Filter */}
          <div className="flex flex-wrap gap-2 mb-8">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors duration-200 ${
                  selectedCategory === category
                    ? 'bg-primary text-white' :'bg-white text-text-secondary hover:bg-secondary-100'
                }`}
              >
                {category === 'all' ? 'All Projects' : category}
              </button>
            ))}
          </div>

          {/* Portfolio Grid */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {paginatedItems.map((item, index) => (
              <div
                key={item.id || index}
                className="bg-white rounded-lg shadow-sm overflow-hidden hover:shadow-md transition-shadow duration-200 cursor-pointer"
                onClick={() => openLightbox(item)}
              >
                {/* Project Thumbnail */}
                <div className="relative h-48 bg-secondary-50">
                  {item.thumbnail ? (
                    <img
                      src={item.thumbnail}
                      alt={item.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="flex items-center justify-center h-full">
                      <Icon
                        name={getProjectTypeIcon(item.project_type || item.projectType)}
                        size={48}
                        className="text-secondary-300"
                      />
                    </div>
                  )}

                  {/* Project Type Badge */}
                  <div className="absolute top-3 left-3 px-2 py-1 bg-white bg-opacity-90 rounded text-xs font-medium">
                    {item.project_type || item.projectType || 'Project'}
                  </div>

                  {/* Featured Badge */}
                  {(item.is_featured || item.isFeatured) && (
                    <div className="absolute top-3 right-3 px-2 py-1 bg-warning bg-opacity-90 rounded text-xs font-medium text-white">
                      Featured
                    </div>
                  )}
                </div>

                {/* Project Info */}
                <div className="p-4">
                  <h3 className="font-medium text-lg mb-1 line-clamp-1">{item.title}</h3>
                  <p className="text-text-secondary text-sm mb-3 line-clamp-2">
                    {item.description || 'No description provided.'}
                  </p>

                  <div className="flex justify-between items-center">
                    <span className="text-xs text-text-muted">
                      {item.category || 'Uncategorized'}
                    </span>

                    {item.client_rating && (
                      <div className="flex items-center gap-1">
                        <Icon name="Star" size={14} className="text-warning fill-current" />
                        <span className="text-xs font-medium">{item.client_rating}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-8">
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="px-4 py-2 rounded-lg border border-border text-text-secondary disabled:opacity-50 disabled:cursor-not-allowed hover:bg-secondary-50 transition-colors"
              >
                <Icon name="ChevronLeft" size={18} />
              </button>

              <div className="flex gap-1">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`w-10 h-10 rounded-lg font-medium transition-colors ${
                      currentPage === page
                        ? 'bg-primary text-white'
                        : 'border border-border text-text-secondary hover:bg-secondary-50'
                    }`}
                  >
                    {page}
                  </button>
                ))}
              </div>

              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="px-4 py-2 rounded-lg border border-border text-text-secondary disabled:opacity-50 disabled:cursor-not-allowed hover:bg-secondary-50 transition-colors"
              >
                <Icon name="ChevronRight" size={18} />
              </button>
            </div>
          )}
        </div>
      )}

      {/* Lightbox Modal */}
      {isLightboxOpen && selectedItem && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-80 z-50 flex items-center justify-center p-4"
          onClick={closeLightbox}
        >
          <div 
            className="bg-white rounded-lg overflow-hidden max-w-5xl w-full max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Lightbox Header */}
            <div className="p-4 border-b border-border flex items-center justify-between">
              <h3 className="font-semibold text-text-primary">{selectedItem.title}</h3>
              <button
                onClick={closeLightbox}
                className="text-text-muted hover:text-text-primary transition-colors"
              >
                <Icon name="X" size={20} />
              </button>
            </div>

            {/* Navigation Buttons */}
            {filteredItems.length > 1 && (
              <div>
                <button
                  onClick={() => navigateLightbox('prev')}
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 z-10 w-10 h-10 bg-black bg-opacity-50 hover:bg-opacity-70 rounded-full flex items-center justify-center transition-colors duration-200"
                >
                  <Icon name="ChevronLeft" size={20} color="white" />
                </button>
                <button
                  onClick={() => navigateLightbox('next')}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 z-10 w-10 h-10 bg-black bg-opacity-50 hover:bg-opacity-70 rounded-full flex items-center justify-center transition-colors duration-200"
                >
                  <Icon name="ChevronRight" size={20} color="white" />
                </button>
              </div>
            )}

            {/* Content */}
            <div className="overflow-y-auto p-6 flex-grow">
              <div className="flex flex-col md:flex-row gap-8">
                {/* Project Image Gallery */}
                <div className="md:w-1/3">
                  <div
                    className="relative rounded-lg overflow-hidden bg-secondary-50 select-none"
                    onTouchStart={handleTouchStart}
                    onTouchEnd={(e) => handleTouchEnd(e, galleryImages.length)}
                  >
                    {galleryImages.length > 0 ? (
                      <>
                        <img
                          key={currentImageIndex}
                          src={galleryImages[currentImageIndex]}
                          alt={`${selectedItem.title} – image ${currentImageIndex + 1}`}
                          className="w-full h-64 object-cover"
                        />
                        {galleryImages.length > 1 && (
                          <>
                            <button
                              onClick={(e) => { e.stopPropagation(); handleImagePrev(); }}
                              disabled={currentImageIndex === 0}
                              className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-black bg-opacity-50 hover:bg-opacity-70 disabled:opacity-30 rounded-full flex items-center justify-center transition-all"
                              aria-label="Previous image"
                            >
                              <Icon name="ChevronLeft" size={16} color="white" />
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); handleImageNext(galleryImages.length); }}
                              disabled={currentImageIndex === galleryImages.length - 1}
                              className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-black bg-opacity-50 hover:bg-opacity-70 disabled:opacity-30 rounded-full flex items-center justify-center transition-all"
                              aria-label="Next image"
                            >
                              <Icon name="ChevronRight" size={16} color="white" />
                            </button>
                          </>
                        )}
                      </>
                    ) : (
                      <div className="h-64 flex items-center justify-center">
                        <Icon
                          name={getProjectTypeIcon(selectedItem.project_type || selectedItem.projectType)}
                          size={80}
                          className="text-secondary-300"
                        />
                      </div>
                    )}
                  </div>
                  {/* Navigation dots */}
                  {galleryImages.length > 1 && (
                    <div className="flex items-center justify-center gap-1.5 mt-3">
                      {galleryImages.map((_, idx) => (
                        <button
                          key={idx}
                          onClick={() => setCurrentImageIndex(idx)}
                          aria-label={`Go to image ${idx + 1}`}
                          className={`rounded-full transition-all duration-200 ${
                            idx === currentImageIndex
                              ? 'w-3 h-3 bg-primary'
                              : 'w-2 h-2 bg-secondary-300 hover:bg-secondary-400'
                          }`}
                        />
                      ))}
                    </div>
                  )}
                  {/* Image counter */}
                  {galleryImages.length > 1 && (
                    <p className="text-center text-xs text-text-muted mt-1">
                      {currentImageIndex + 1} / {galleryImages.length}
                    </p>
                  )}
                </div>

                {/* Project Details */}
                <div className="md:w-2/3">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <Icon
                        name={getProjectTypeIcon(selectedItem.project_type || selectedItem.projectType)}
                        size={20}
                        className="text-primary"
                      />
                      <p className="text-accent font-medium">{selectedItem.category || 'Uncategorized'}</p>
                    </div>
                    {selectedItem.client_rating && (
                      <div className="flex items-center gap-1">
                        <Icon name="Star" size={16} className="text-warning fill-current" />
                        <span className="font-medium">{selectedItem.client_rating}</span>
                      </div>
                    )}
                  </div>

                  <p className="text-text-secondary mb-6 leading-relaxed">
                    {selectedItem.full_description || selectedItem.description || 'No description provided.'}
                  </p>

                  {/* Project Details */}
                  <div className="space-y-4 mb-6">
                    <div className="flex items-center gap-3">
                      <Icon name="User" size={16} className="text-text-muted" />
                      <span className="text-sm text-text-secondary">Client: {selectedItem.client || 'Not specified'}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Icon name="Calendar" size={16} className="text-text-muted" />
                      <span className="text-sm text-text-secondary">Completed: {selectedItem.completed_date || selectedItem.date || 'Not specified'}</span>
                    </div>
                    {(selectedItem.duration || (selectedItem.start_date && selectedItem.end_date)) && (
                      <div className="flex items-center gap-3">
                        <Icon name="Clock" size={16} className="text-text-muted" />
                        <span className="text-sm text-text-secondary">Duration: {selectedItem.duration || `${selectedItem.start_date} - ${selectedItem.end_date || 'Present'}`}</span>
                      </div>
                    )}
                  </div>

                  {/* Technologies */}
                  {selectedItem.technologies && selectedItem.technologies.length > 0 && (
                    <div className="mb-6">
                      <h4 className="font-medium text-text-primary mb-2">Technologies Used:</h4>
                      <div className="flex flex-wrap gap-2">
                        {selectedItem.technologies.map((tech, index) => (
                          <span
                            key={index}
                            className="px-3 py-1 bg-primary-100 text-primary text-sm rounded-full"
                          >
                            {typeof tech === 'object' ? tech.name : tech}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex gap-3">
                    {(selectedItem.live_url || selectedItem.liveUrl) && (
                      <Button
                        variant="primary"
                        className="flex items-center gap-2"
                        onClick={(e) => {
                          e.stopPropagation();
                          window.open(selectedItem.live_url || selectedItem.liveUrl, '_blank');
                        }}
                      >
                        <Icon name="ExternalLink" size={16} />
                        View Live
                      </Button>
                    )}
                    {(selectedItem.source_url || selectedItem.sourceUrl) && (
                      <Button
                        variant="outline"
                        className="flex items-center gap-2"
                        onClick={(e) => {
                          e.stopPropagation();
                          window.open(selectedItem.source_url || selectedItem.sourceUrl, '_blank');
                        }}
                      >
                        <Icon name="Github" size={16} />
                        Source Code
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default PortfolioGallery;
